export const PERIOD_OPTIONS = [
  { value: "all_time", label: "All time" },
  { value: "year_to_date", label: "Year to date" },
  { value: "previous_year_to_date", label: "Previous year to date" },
  { value: "last_12_months", label: "Last 12 months" },
  { value: "last_24_months", label: "Last 24 months" },
  { value: "custom", label: "Custom" },
] as const;

export type PeriodPreset = (typeof PERIOD_OPTIONS)[number]["value"];
export type DatePreset = Exclude<PeriodPreset, "custom">;
export type PeriodDates = { since: string | null; until: string | null };

/** The current instant, kept behind a seam so callers can pin it in tests. */
export function today(): Date {
  return new Date();
}

function isoDate(year: number, month: number, day: number): string {
  return `${String(year).padStart(4, "0")}-${String(month + 1).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
}

function utcParts(value: Date | string): { year: number; month: number; day: number } {
  const date = typeof value === "string" ? new Date(`${value.slice(0, 10)}T00:00:00Z`) : value;
  return { year: date.getUTCFullYear(), month: date.getUTCMonth(), day: date.getUTCDate() };
}

export function presetDates(preset: DatePreset, currentDay: Date | string): PeriodDates {
  const { year, month, day } = utcParts(currentDay);
  if (preset === "all_time") return { since: null, until: null };
  if (preset === "year_to_date") return { since: isoDate(year, 0, 1), until: null };
  if (preset === "previous_year_to_date") {
    const previousYear = year - 1;
    const lastDayOfMonth = new Date(Date.UTC(previousYear, month + 1, 0)).getUTCDate();
    return { since: isoDate(previousYear, 0, 1), until: isoDate(previousYear, month, Math.min(day, lastDayOfMonth)) };
  }
  const months = preset === "last_12_months" ? 12 : 24;
  const start = new Date(Date.UTC(year, month - months, 1));
  return { since: isoDate(start.getUTCFullYear(), start.getUTCMonth(), 1), until: null };
}

export function presetFor(since: string | null | undefined, until: string | null | undefined, currentDay: Date | string): PeriodPreset {
  const normalized = { since: since || null, until: until || null };
  const presets: DatePreset[] = ["all_time", "year_to_date", "previous_year_to_date", "last_12_months", "last_24_months"];
  return presets.find((preset) => {
    const dates = presetDates(preset, currentDay);
    return dates.since === normalized.since && dates.until === normalized.until;
  }) ?? "custom";
}
